/**
 * BATON WebSocket reconnect policy — backoff, auth terminal closes, attempt caps.
 */

export const BATON_RECONNECT_POLICY = {
  BASE_DELAY_MS: 1000,
  MAX_DELAY_MS: 60_000,
  MAX_ATTEMPTS_PER_CYCLE: 8,
  MAX_COOLDOWN_CYCLES: 5,
  COOLDOWN_MS: 30_000,
  NATIVE_BASE_DELAY_MS: 5000,
  NATIVE_MAX_ATTEMPTS: 3,
  NATIVE_COOLDOWN_MS: 60_000,
  /** After this many consecutive WS failures since last connect, force HTTP auth validation. */
  AUTH_PROBE_AFTER_CONSECUTIVE_FAILURES: 4,
} as const;

/**
 * WebSocket close codes that explicitly signal auth rejection in the reason field.
 *
 * Do NOT add 1006 here. Abnormal closure (1006) is the dominant code for network
 * drops *and* for burned-token storms when the server/proxy drops the TCP socket
 * without a policy close frame — retrying 1006 blindly would false-positive network
 * outages. Burned-token / 401 refresh rejection is handled independently via
 * api.ts → markSessionDead → maestro:session-expired → haltReconnectLoops().
 */
const AUTH_CLOSE_CODES = new Set([
  1008, // policy violation
  4001,
  4401,
  4403,
  4404,
]);

const AUTH_REASON_RE = /unauthorized|invalid\s*token|token\s*expired|jwt|forbidden|\b401\b|\b403\b|auth(entication)?\s*(failed|required|rejected)/i;

export function computeReconnectDelayMs(
  attempt: number,
  baseDelayMs = BATON_RECONNECT_POLICY.BASE_DELAY_MS,
  maxDelayMs = BATON_RECONNECT_POLICY.MAX_DELAY_MS,
): number {
  const exp = baseDelayMs * 2 ** Math.max(0, attempt - 1);
  const capped = Math.min(exp, maxDelayMs);
  const jitter = 0.5 + Math.random() * 0.5;
  return Math.round(capped * jitter);
}

export function isAuthWebSocketClose(code: number, reason: string): boolean {
  if (AUTH_CLOSE_CODES.has(code)) return true;
  const trimmed = String(reason || '').trim();
  if (!trimmed) return false;
  return AUTH_REASON_RE.test(trimmed);
}

export function isAbnormalClosure(code: number): boolean {
  return code === 1006;
}

/** When JWT is still valid, repeated WS failures are transport — not auth. */
export function shouldProbeAuthAfterWsFailures(canConnect: boolean): boolean {
  return !canConnect;
}
